import React from 'react';
import { Edit2, Plus, X, Trash2 } from 'lucide-react';

interface DepthString {
  id: number;
  name: string;
  active: boolean;
}

interface DepthStringManagerProps {
  activeTab: string;
  depthStrings: DepthString[];
  editingDepthString: number | null;
  editedDepthStringName: string;
  setEditedDepthStringName: (name: string) => void;
  handleDepthStringEdit: (id: number) => void;
  saveDepthStringEdit: () => void;
  toggleDepthStringActive: (id: number) => void;
  addNewDepthString: () => void;
  onClose: () => void;
}

const DepthStringManager: React.FC<DepthStringManagerProps> = ({
  activeTab,
  depthStrings,
  editingDepthString,
  editedDepthStringName,
  setEditedDepthStringName,
  handleDepthStringEdit,
  saveDepthStringEdit,
  toggleDepthStringActive,
  addNewDepthString,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        id="depth-string-manager" 
        className="bg-white rounded-lg shadow-lg p-4 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h3 className="text-lg font-semibold">Manage Depth Strings</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Add, rename, or remove depth strings for {activeTab}. Use the checkbox to show/hide each string.
        </p>
        
        <div className="max-h-60 overflow-y-auto mb-4">
          {depthStrings.map(depthString => (
            <div 
              key={depthString.id} 
              className="flex items-center justify-between py-2 px-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={depthString.active}
                  onChange={() => toggleDepthStringActive(depthString.id)}
                  className="w-4 h-4 text-primary rounded focus:ring-primary"
                  id={`depth-string-${depthString.id}`}
                />
                <label htmlFor={`depth-string-${depthString.id}`} className="select-none">
                  {editingDepthString === depthString.id ? (
                    <input
                      type="text"
                      value={editedDepthStringName}
                      onChange={e => setEditedDepthStringName(e.target.value)}
                      onBlur={saveDepthStringEdit}
                      onKeyDown={e => e.key === 'Enter' && saveDepthStringEdit()}
                      className="w-full p-1 border border-gray-300 rounded text-sm"
                      autoFocus
                    />
                  ) : (
                    <span>{depthString.name}</span>
                  )}
                </label>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleDepthStringEdit(depthString.id)}
                  className="p-1 text-gray-500 hover:text-primary rounded-full hover:bg-gray-100"
                  title="Edit depth string name"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {depthStrings.length > 1 && (
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this depth string?')) {
                        toggleDepthStringActive(depthString.id);
                      }
                    }}
                    className="p-1 text-gray-500 hover:text-red-500 rounded-full hover:bg-gray-100"
                    title="Remove depth string"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex gap-2 pt-2 border-t">
          <button
            onClick={addNewDepthString}
            className="flex-1 py-2 px-4 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add New Depth String
          </button>
          <button
            onClick={onClose}
            className="py-2 px-4 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepthStringManager; 